import { usePostTablesRoles } from '@ipfabric/api-client/client/model/rbac-—-roles/rbac-—-roles';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  RoleGetResponseBodyV2,
  RolePutRequestBodyV2,
  RolesPostRequestBodyV2,
} from '@ipfabric/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'utils/request/useApi';
import { useTableQueryWithMandatoryColumns } from 'utils/table/useTableQueryWithMandatoryColumns';

/** Roles v2 adds endpoint/data policy links; it is selected via this header. */
const V2_OPTIONS = { headers: { 'x-api-version': '2' } } as const;

export const useRolesTableQuery = () => {
  const rolesQueryRaw = usePostTablesRoles({
    columns: [
      'id',
      'name',
      'description',
      'type',
      'isAdmin',
      'isSystem',
      'endpointPolicies',
      'dataPolicies',
    ],
  });
  return useTableQueryWithMandatoryColumns(['id', 'name'], rolesQueryRaw);
};

export const useRoleQuery = (roleId: string | null) => {
  const api = useApi();
  return useQuery({
    ...api.query<RoleGetResponseBodyV2>({
      method: 'GET',
      url: `/roles/${encodeURIComponent(roleId ?? '')}`,
      options: V2_OPTIONS,
    }),
    enabled: roleId !== null,
    // Detail keys are request objects (not URL-prefixed), so always reload on open.
    refetchOnMount: 'always',
  });
};

export type RoleSaveBody = RolesPostRequestBodyV2 & RolePutRequestBodyV2;

export const useRolesV2Api = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useStableCallback(() =>
    Promise.all(
      ['/tables/roles', '/data-policies', '/endpoint-policies'].map((url) =>
        queryClient.invalidateQueries({ queryKey: [url] }),
      ),
    ),
  );

  const createRole = useStableCallback(async (body: RoleSaveBody) => {
    await api.request({
      method: 'POST',
      url: '/roles',
      data: body,
      options: V2_OPTIONS,
    });
    await invalidate();
  });

  const updateRole = useStableCallback(
    async (roleId: string, body: RoleSaveBody) => {
      await api.request({
        method: 'PUT',
        url: `/roles/${encodeURIComponent(roleId)}`,
        data: body,
        options: V2_OPTIONS,
      });
      await invalidate();
    },
  );

  const deleteRole = useStableCallback(async (roleId: string) => {
    await api.request({
      method: 'DELETE',
      url: `/roles/${encodeURIComponent(roleId)}`,
    });
    await invalidate();
  });

  return { createRole, updateRole, deleteRole };
};
